import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { ExternalLink, Loader2, Scale } from 'lucide-react';
import { ipdApi, type IpdExcessCollectionRow } from '@/api/ipd';
import { useAuth } from '@/contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';

type StatusFilter = 'ALL' | 'ADMITTED' | 'DISCHARGED';

function formatInr(n: number) {
  return `₹${Number(n).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

function formatIstDate(iso: string | null | undefined) {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleDateString('en-IN', {
    timeZone: 'Asia/Kolkata',
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });
}

export default function IpdExcessCollectionPage() {
  const navigate = useNavigate();
  const { activeHospitalId, user, isChainAdmin } = useAuth();
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('ALL');

  const hospitalIdParam =
    isChainAdmin && activeHospitalId ? activeHospitalId : user?.hospitalId;

  const queryKey = useMemo(
    () => ['ipd-excess-collections', statusFilter, hospitalIdParam] as const,
    [statusFilter, hospitalIdParam]
  );

  const { data, isLoading, isFetching } = useQuery({
    queryKey,
    queryFn: async () => {
      const res = await ipdApi.listExcessCollections({
        status: statusFilter,
        ...(hospitalIdParam ? { hospitalId: hospitalIdParam } : {}),
      });
      return res.data?.data;
    },
    enabled: !!hospitalIdParam || !isChainAdmin,
  });

  const rows: IpdExcessCollectionRow[] = data?.rows ?? [];

  return (
    <div className="p-6 space-y-6">
      <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="font-artis-serif text-2xl font-semibold text-artis-brown-dark flex items-center gap-2">
            <Scale className="h-7 w-7 text-artis-brown-dark/80" />
            IPD excess collection
          </h1>
          <p className="text-sm text-muted-foreground mt-1 max-w-2xl">
            Admissions where the REVENUE discharge bill amount is lower than effective collection
            (desk deposits and settled third-party), same rules as the Expenses IPD billing summary.
          </p>
        </div>
        <Select value={statusFilter} onValueChange={(v) => setStatusFilter(v as StatusFilter)}>
          <SelectTrigger className="w-[180px]">
            <SelectValue placeholder="Admission status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="ALL">All (admitted + discharged)</SelectItem>
            <SelectItem value="ADMITTED">Currently admitted</SelectItem>
            <SelectItem value="DISCHARGED">Discharged</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-lg">Over-collected IPD cases</CardTitle>
          {data?.scannedAdmissionCount != null && (
            <p className="text-xs text-muted-foreground">
              Scanned {data.scannedAdmissionCount} recent admission(s) with a revenue bill; showing{' '}
              {rows.length} with excess collection.
            </p>
          )}
        </CardHeader>
        <CardContent>
          {isChainAdmin && !hospitalIdParam ? (
            <p className="text-sm text-amber-800">
              Select a hospital from the header to load this report.
            </p>
          ) : isLoading ? (
            <div className="flex items-center gap-2 text-muted-foreground py-8">
              <Loader2 className="h-5 w-5 animate-spin" />
              Loading…
            </div>
          ) : rows.length === 0 ? (
            <p className="text-sm text-muted-foreground py-6">
              No excess collection cases found in the scanned admissions.
            </p>
          ) : (
            <div className="rounded-md border overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>IPD #</TableHead>
                    <TableHead>Patient</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Admitted</TableHead>
                    <TableHead>Discharged</TableHead>
                    <TableHead className="text-right">Billed</TableHead>
                    <TableHead className="text-right">Collected</TableHead>
                    <TableHead className="text-right">Excess</TableHead>
                    <TableHead>Bill</TableHead>
                    <TableHead />
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {rows.map((row) => (
                    <TableRow key={row.admissionId}>
                      <TableCell className="font-mono text-xs">{row.ipdNumber}</TableCell>
                      <TableCell>
                        <div className="font-medium">{row.patientName}</div>
                        {row.uhid ? (
                          <div className="text-xs text-muted-foreground">{row.uhid}</div>
                        ) : null}
                      </TableCell>
                      <TableCell>
                        <Badge variant="outline" className="text-xs">
                          {row.admissionStatus}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-sm whitespace-nowrap">
                        {formatIstDate(row.admissionDate)}
                      </TableCell>
                      <TableCell className="text-sm whitespace-nowrap">
                        {formatIstDate(row.dischargeDate)}
                      </TableCell>
                      <TableCell className="text-right tabular-nums">
                        {formatInr(row.billedAmount)}
                      </TableCell>
                      <TableCell className="text-right tabular-nums">
                        {formatInr(row.collectedAmount)}
                      </TableCell>
                      <TableCell className="text-right tabular-nums font-medium text-blue-800">
                        {formatInr(row.excessAmount)}
                      </TableCell>
                      <TableCell className="text-xs">
                        {row.billNumber ?? '—'}
                        {row.billStatus ? (
                          <span className="block text-muted-foreground">{row.billStatus}</span>
                        ) : null}
                      </TableCell>
                      <TableCell>
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          className="h-8 gap-1"
                          onClick={() =>
                            navigate(`/hospital-admin/ipd/${row.admissionId}/billing`)
                          }
                        >
                          Billing
                          <ExternalLink className="h-3.5 w-3.5" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
          {isFetching && !isLoading ? (
            <p className="text-xs text-muted-foreground mt-2">Refreshing…</p>
          ) : null}
        </CardContent>
      </Card>
    </div>
  );
}
